import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Response_Processing from "../../../../src/layout/Others/Response_Processing";
import { toast } from "react-toastify";
import MessageLabel from "../../../components/CommonComponents/MessageLabel";
import AdminBackNavigation from "../../../components/CommonComponents/AdminBackNavigation";

const PartnerCreate = () => {
  // Refs for form elements
  const usernameRef = useRef(null);
  const last_nameRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const roleRef = useRef(null);
  const ccodeRef = useRef(null);
  const mobileRef = useRef(null);
  const percentageRef = useRef(null);
  const bankNameRef = useRef(null);
  const branchNameRef = useRef(null);
  const upiMobileRef = useRef(null);
  const accountNumberRef = useRef(null);
  const ifscCodeRef = useRef(null);
  const upiIdRef = useRef(null);

  const navigate = useNavigate();

  // State management
  const [role, setRole] = useState([]);
  const [allpartner, setAllPartners] = useState([]);
  const [countrycode, setCountryCode] = useState([]);
  const [inputs, setInputs] = useState({
    username: '',
    last_name: '',
    email: '',
    password: '',
    ccode: '',
    mobile_number: '',
    role_id: '',
    commission_percentage: '',
    bank_name: '',
    branch_name: '',
    account_number: '',
    IFSC_Code: '',
    upi_id: '',
    upi_mobile_number: ''
  });
  const [errors, setErrors] = useState({});
  const [processing, setProcessing] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [numberValue, setNumberValue] = useState("");
  const [switchTap, setSwitchTap] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [maxMobileLength, setMaxMobileLength] = useState(16);

  // Authentication headers
  const access_token = localStorage.getItem("access_token");
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${access_token}`,
    Accept: "application/json",
    "Access-Control-Allow-Origin": "*",
  };

  // Handle mobile number input with validation based on country
  const handleInputChange = useCallback((e) => {
    const input = e.target.value;
    const name = e.target.name;
    const sanitizedInput = input.replace(/\D/g, "").slice(0, maxMobileLength);

    setNumberValue(sanitizedInput);
    setInputs(prev => ({ ...prev, [name]: sanitizedInput }));

    // Clear error when user reaches the expected length for selected country
    if (sanitizedInput.length === maxMobileLength) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  }, [maxMobileLength]);

  // Fetch initial data (roles, countries, partners)
  const fetchInitialData = useCallback(async () => {
    try {
      console.log("Fetching initial data...");
      const response = await axios.get(
        `${process.env.REACT_APP_Baseurl}/admin/partner/create`,
        { headers }
      );

      const responseData = response?.data || {};
      console.log("API Response:", responseData);

      const { partnerRoles, country, allPartners } = responseData;

      // Set country codes
      if (Array.isArray(country)) {
        console.log("Country data loaded:", country.length, "countries");
        setCountryCode(country);

        // Set default country (e.g., first country or a specific one)
        const defaultCountry = country.find(c => c.phonecode === '91') || country[0]; // Default to India or first country
        if (defaultCountry) {
          setSelectedCountry(defaultCountry);
          setMaxMobileLength(defaultCountry.mobile_length || 10);
          console.log("Default country set:", defaultCountry.country_name, "Mobile length:", defaultCountry.mobile_length || 10);
        }
      } else {
        console.warn("Country data is not an array:", country);
        setCountryCode([]);
      }

      // Set all partners
      if (Array.isArray(allPartners)) {
        console.log("Partners data loaded:", allPartners.length, "partners");
        setAllPartners(allPartners);
      } else {
        console.warn("Partners data is not an array:", allPartners);
        setAllPartners([]);
      }

      // Set roles with proper validation
      if (Array.isArray(partnerRoles)) {
        console.log("Roles data loaded:", partnerRoles.length, "roles");
        setRole(partnerRoles);
      } else if (partnerRoles && typeof partnerRoles === 'object') {
        console.log("Converting single role to array:", partnerRoles);
        setRole([partnerRoles]);
      } else {
        console.warn("PartnerRoles data is not in expected format:", partnerRoles);
        setRole([]);
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      toast.error("Failed to load form data. Please refresh and try again.");
    }
  }, [headers]);
  // Initialize component data
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);
  // Handle general input changes
  const handleChange = useCallback((event) => {
    const { name, value } = event.target;

    setInputs(prevValues => ({
      ...prevValues,
      [name]: value
    }));

    // Handle country code change
    if (name === 'ccode') {
      const selectedCountryData = countrycode.find(country => country.phonecode === value);
      if (selectedCountryData) {
        setSelectedCountry(selectedCountryData);
        const mobileLength = selectedCountryData.mobile_length || 10; // Default to 10 if not specified
        setMaxMobileLength(mobileLength);
        console.log(`Country changed to: ${selectedCountryData.country_name}, Mobile length: ${mobileLength}`);

        // Clear mobile number if it exceeds new country's length
        if (numberValue.length > mobileLength) {
          const truncatedNumber = numberValue.slice(0, mobileLength);
          setNumberValue(truncatedNumber);
          setInputs(prev => ({ ...prev, mobile_number: truncatedNumber }));
          toast.info(`Mobile number truncated to ${mobileLength} digits for ${selectedCountryData.country_name}`);
        }

        // Clear mobile number error when country changes
        if (errors.mobile_number) {
          setErrors(prev => ({ ...prev, mobile_number: "" }));
        }
      }
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  }, [errors, countrycode, numberValue]);

  // Toggle password visibility
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);
  // Validation patterns
  const EMAIL_PATTERN = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  const PHONE_PATTERN = /^\d{10}$/;
  const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  const UPI_PATTERN = /^[\w.-]{2,256}@[a-zA-Z]{2,64}$/;
  const BANK_NAME_PATTERN = /^[A-Za-z\s]+$/;
  const DIGITS_ONLY_PATTERN = /^\d+$/;
  // Validate first tab (User Information)
  const validateFirstTab = useCallback(() => {
    const newErrors = {};

    // Username validation
    if (!inputs.username?.trim()) {
      newErrors.username = "First name is required";
      usernameRef.current?.focus();
    } else if (inputs.username.trim().length < 2) {
      newErrors.username = "First name must be at least 2 characters";
      usernameRef.current?.focus();
    }

    // Last name validation
    if (!inputs.last_name?.trim()) {
      newErrors.last_name = "Last name is required";
      last_nameRef.current?.focus();
    } else if (inputs.last_name.trim().length < 2) {
      newErrors.last_name = "Last name must be at least 2 characters";
      last_nameRef.current?.focus();
    }

    // Email validation
    if (!inputs.email?.trim()) {
      newErrors.email = "Email is required";
      emailRef.current?.focus();
    } else if (!EMAIL_PATTERN.test(inputs.email.trim())) {
      newErrors.email = "Please enter a valid email address";
      emailRef.current?.focus();
    }

    // Password validation
    if (!inputs.password) {
      newErrors.password = "Password is required";
      passwordRef.current?.focus();
    } else if (inputs.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
      passwordRef.current?.focus();
    }

    // Country code validation
    if (!inputs.ccode) {
      newErrors.ccode = "Country code is required";
      ccodeRef.current?.focus();
    }

    // Mobile number validation based on selected country
    if (!numberValue) {
      newErrors.mobile_number = "Mobile number is required";
      mobileRef.current?.focus();
    } else {
      const expectedLength = selectedCountry?.mobile_length || maxMobileLength;
      const countryName = selectedCountry?.country_name || 'selected country';

      if (numberValue.length < expectedLength) {
        newErrors.mobile_number = `Mobile number must be ${expectedLength} digits for ${countryName}`;
        mobileRef.current?.focus();
      } else if (numberValue.length > expectedLength) {
        newErrors.mobile_number = `Mobile number cannot exceed ${expectedLength} digits for ${countryName}`;
        mobileRef.current?.focus();
      }
    }

    // Role validation
    if (!inputs.role_id) {
      newErrors.role_id = "User role is required";
      roleRef.current?.focus();
    }

    // Commission percentage validation
    if (!inputs.commission_percentage?.trim()) {
      newErrors.commission_percentage = "Commission percentage is required";
      percentageRef.current?.focus();
    } else {
      const commission = parseFloat(inputs.commission_percentage);
      if (isNaN(commission)) {
        newErrors.commission_percentage = "Commission must be a valid number";
        percentageRef.current?.focus();
      } else if (commission < 0 || commission > 100) {
        newErrors.commission_percentage = "Commission must be between 0 and 100";
        percentageRef.current?.focus();
      }
    }

    return newErrors;
  }, [inputs, numberValue, EMAIL_PATTERN, PHONE_PATTERN]);

  // Validate all form fields (both tabs)
  const validateAll = useCallback(() => {
    const newErrors = validateFirstTab();

    // Bank name validation
    if (!inputs.bank_name?.trim()) {
      newErrors.bank_name = "Bank name is required";
      bankNameRef.current?.focus();
    } else if (!BANK_NAME_PATTERN.test(inputs.bank_name.trim())) {
      newErrors.bank_name = "Bank name should contain only letters and spaces";
      bankNameRef.current?.focus();
    }

    // Branch name validation (optional but if provided should be valid)
    if (inputs.branch_name?.trim() && !BANK_NAME_PATTERN.test(inputs.branch_name.trim())) {
      newErrors.branch_name = "Branch name should contain only letters and spaces";
      branchNameRef.current?.focus();
    }

    // Account number validation
    if (!inputs.account_number?.trim()) {
      newErrors.account_number = "Account number is required";
      accountNumberRef.current?.focus();
    } else {
      const accNum = inputs.account_number.trim();
      if (!DIGITS_ONLY_PATTERN.test(accNum)) {
        newErrors.account_number = "Account number must contain only digits";
        accountNumberRef.current?.focus();
      } else if (accNum.length < 9 || accNum.length > 18) {
        newErrors.account_number = "Account number must be between 9 and 18 digits";
        accountNumberRef.current?.focus();
      }
    }

    // IFSC code validation
    if (!inputs.IFSC_Code?.trim()) {
      newErrors.IFSC_Code = "IFSC code is required";
      ifscCodeRef.current?.focus();
    } else {
      const ifsc = inputs.IFSC_Code.trim().toUpperCase();
      if (!IFSC_PATTERN.test(ifsc)) {
        newErrors.IFSC_Code = "Please enter a valid IFSC code (e.g., HDFC0123456)";
        ifscCodeRef.current?.focus();
      }
    }

    // UPI ID validation (optional)
    if (inputs.upi_id?.trim()) {
      const upi = inputs.upi_id.trim();
      if (!UPI_PATTERN.test(upi)) {
        newErrors.upi_id = "Please enter a valid UPI ID (e.g., user@bank)";
        upiIdRef.current?.focus();
      }
    }

    // UPI mobile number validation (optional) based on selected country
    if (inputs.upi_mobile_number?.trim()) {
      const upiMobile = inputs.upi_mobile_number.trim();
      const expectedLength = selectedCountry?.mobile_length || maxMobileLength;
      const countryName = selectedCountry?.country_name || 'selected country';

      if (!DIGITS_ONLY_PATTERN.test(upiMobile)) {
        newErrors.upi_mobile_number = "UPI mobile number must contain only digits";
        upiMobileRef.current?.focus();
      } else if (upiMobile.length !== expectedLength) {
        newErrors.upi_mobile_number = `UPI mobile number must be exactly ${expectedLength} digits for ${countryName}`;
        upiMobileRef.current?.focus();
      }
    }

    return newErrors;
  }, [validateFirstTab, inputs, BANK_NAME_PATTERN, DIGITS_ONLY_PATTERN, IFSC_PATTERN, UPI_PATTERN]);

  // Handle tab switching with validation
  const handleValidate = useCallback(() => {
    const firstTabErrors = validateFirstTab();
    setErrors(firstTabErrors);

    if (Object.keys(firstTabErrors).length === 0) {
      setSwitchTap(1);
      toast.success("Step 1 completed successfully! Please fill in the account details.");
    } else {
      // toast.error("Please fill the mandatory fields before proceeding to the next step.");
    }
  }, [validateFirstTab]);


  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Handle step navigation or final submission
    if (switchTap === 0) {
      handleValidate();
      return;
    }

    // Final submission validation
    const allErrors = validateAll();
    setErrors(allErrors);

    if (Object.keys(allErrors).length === 0) {
      try {
        setProcessing(true);
        setShowOverlay(true);

        const submitData = {
          ...inputs,
          mobile_number: numberValue,
          status: 1,
        };

        const response = await axios.post(
          `${process.env.REACT_APP_Baseurl}/admin/partner/store`,
          submitData,
          { headers }
        );

        const result = response?.data;
        if (result?.status) {
          toast.success(result?.message || "Partner created successfully!");
          navigate("/partner-index");
        } else {
          throw new Error(result?.message || "Failed to create partner");
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || "Something went wrong!";
        toast.error(errorMessage);
        console.error("Partner creation error:", err);
      } finally {
        setProcessing(false);
        setShowOverlay(false);
      }
    } else {
      // toast.error("Please fix all errors before submitting.");
    }
  }, [switchTap, handleValidate, validateAll, inputs, numberValue, headers, navigate]);



  return (
    <>
      <div>
        {showOverlay && <Response_Processing></Response_Processing>}
      </div>

      {<AdminBackNavigation path={`${'/partner-index'}`} text="Back" />}

      <section className="section container-fluid mt-3">
        <div id="content-page" className="content-page">
          <div className="iq-card p-3 mt-3">
            <div className="p-3">
              <h4 className="d-flex align-items-center">
                <i className="entypo-archive me-2"></i> Add New Partner
              </h4>
              <hr />
              <div className="d-flex flex-wrap gap-3 mb-4">
                <button className={`tabHeading p-2 ${switchTap === 0 ? "active" : ""}`} onClick={() => setSwitchTap(0)} >
                  User
                </button>
                <button className={`tabHeading p-2 ${switchTap === 1 ? "active" : ""}`} onClick={handleValidate} >
                  Account
                </button>
              </div>

              <form id="Moderator_form" onSubmit={handleSubmit}>
                {/* User Information Form */}
                <div className={`row p-3 ${switchTap === 0 ? "d-flex" : "d-none"}`}>
                  {/* User Name */}
                  <div className="col-md-6 mb-3">
                    <MessageLabel text={"First Name"} className="mandatorySimple" />
                    <input id="name" type="text" className="form-control" name="username" autoComplete="username" onChange={handleChange} ref={usernameRef} />
                    {errors.username && <span className="text-danger mt-2 d-block">{errors.username}</span>}
                  </div>

                  {/* Last Name */}
                  <div className="col-md-6 mb-3">
                    <MessageLabel text={"Last Name"} className="mandatorySimple" />
                    <input id="last_name" type="text" className="form-control" name="last_name" autoComplete="last_name" onChange={handleChange} ref={last_nameRef} />
                    {errors.last_name && <span className="text-danger mt-2 d-block">{errors.last_name}</span>}
                  </div>

                  {/* Email */}
                  <div className="col-md-6 mb-3">
                    <MessageLabel text={"Email Address"} className="mandatorySimple" />
                    <input id="email" type="email" className="form-control" name="email" autoComplete="email" onChange={handleChange} ref={emailRef} />
                    {errors.email && <span className="text-danger mt-2 d-block">{errors.email}</span>}
                  </div>

                  {/* Password */}
                  <div className="col-md-6 mb-3">
                    <MessageLabel text={"Password"} className="mandatorySimple" />
                    <div className="position-relative">
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        className="form-control pe-5"
                        name="password"
                        autoComplete="password"
                        onChange={handleChange}
                        ref={passwordRef}
                        placeholder="Enter password"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary position-absolute end-0 top-0 h-100 px-3 border-0"
                        onClick={togglePasswordVisibility}
                        style={{
                          background: 'transparent',
                          borderLeft: '1px solid #ced4da',
                          borderRadius: '0 0.375rem 0.375rem 0'
                        }}
                        title={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
                            <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
                            <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.708zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
                          </svg>
                        ) : (
                          <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
                            <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.password && <span className="text-danger mt-2 d-block">{errors.password}</span>}
                  </div>

                  {/* Country Code & Mobile Number */}
                  <div className="col-md-6 mb-3">
                    <MessageLabel text={"User's Mobile"} className="mandatorySimple" />
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="col-4">
                        <select name="ccode" value={inputs?.ccode || ''} onChange={handleChange} className="form-select mb-3 custom-placeholder border border-1 theme-border-color theme-text-color" ref={ccodeRef}>
                          <option value='' disabled>Select Country Code</option>
                          {countrycode && countrycode.length > 0 ? (
                            countrycode.map((item, index) => (
                              <option
                                key={item?.id || index}
                                value={item?.phonecode}
                                className="theme-bg-color theme-text-color"
                                title={`Mobile length: ${item?.mobile_length} digits`}
                              >
                                {item?.country_name} (+{item?.phonecode})
                              </option>
                            ))
                          ) : (
                            <option value='' disabled>Loading countries...</option>
                          )}
                        </select>
                        {errors.ccode && <span className="text-danger mt-2 d-block">{errors.ccode}</span>}
                        {/* {selectedCountry && (
                          <small className="text-muted">
                            {selectedCountry.country_name}: {maxMobileLength} digits
                          </small>
                        )} */}
                      </div>
                      <div className="col-8 ms-2">
                        <input
                          type="number"
                          name="mobile_number"
                          value={numberValue}
                          onChange={handleInputChange}
                          placeholder={`Enter mobile number`}
                          ref={mobileRef}
                          className="rs-input py-3 px-3 custom-placeholder theme-text-color border border-1 theme-border-color mb-3"
                          maxLength={maxMobileLength}
                        />
                        {errors.mobile_number && <span className="text-danger mt-2 d-block">{errors.mobile_number}</span>}
                      </div>
                    </div>
                  </div>


                  {/* User Role */}
                  <div className="col-md-6 mb-3">
                    <MessageLabel text={"User Role"} className="mandatorySimple" />
                    <select className="form-select" name="role_id" onChange={handleChange} value={inputs?.role_id} ref={roleRef}>
                      <option value="">Select Role</option>
                      {role?.map((roles, index) => (
                        <option key={index} value={roles.id}>{roles.role_name}</option>
                      ))}
                    </select>
                    {errors.role_id && <span className="text-danger mt-2 d-block">{errors.role_id}</span>}
                  </div>

                  {/* Commission Percentage */}
                  <div className="col-md-6 mb-3">
                    <MessageLabel text={"Commission Percentage"} className="mandatorySimple" />
                    {/* <input id="commission_percentage" type="text" className="form-control" name="commission_percentage" onChange={handleChange} ref={percentageRef} /> */}
                    <input id="commission_percentage" type="text" className="form-control" name="commission_percentage" onChange={handleChange} ref={percentageRef} />
                    {errors.commission_percentage && <span className="text-danger mt-2 d-block">{errors.commission_percentage}</span>}
                  </div>



                  {/* Assign to Partner */}
                  {/* <div className="col-md-6 mb-3">
                    <label htmlFor="assign_partner" style={{ marginBottom: "8px", padding: "4px 8px", display: "block" }}>Assign To Partner <span className="mandatory">*</span></label>
                    <select className="form-select" name="assign_partner" onChange={handleChange} value={inputs?.partner_id} ref={roleRef}>
                      <option value="">Select Partner</option>
                      {allpartner?.map((value, index) => (
                        <option key={index} value={value.id}>{value.username}</option>
                      ))}
                    </select>
                  </div> */}

                  {/* Navigation Buttons */}
                  <div className="col-12 d-flex justify-content-between align-items-center mt-4">
                    <div></div>
                    <button
                      type="button"
                      onClick={handleValidate}
                      className="btn btn-primary fw-bold px-4"
                      disabled={processing}
                    >
                      Next Step →
                    </button>
                  </div>
                </div>

                {/* Account Details Form */}

                <div className={`row p-3 ${switchTap === 0 ? "d-none" : "d-flex"}`}>
                  {/* Bank Name */}
                  <div className="col-md-6 mb-3">
                    <MessageLabel text={"Bank Name"} className="mandatorySimple" />
                    <input
                      id="bank_name"
                      type="text"
                      className="form-control"
                      name="bank_name"
                      autoComplete="bank_name"
                      placeholder=""
                      onChange={handleChange}
                      ref={bankNameRef}
                    />
                    {errors.bank_name && <span className="text-danger mt-2 d-block">{errors.bank_name}</span>}
                  </div>

                  {/* Branch Name */}
                  <div className="col-md-6 mb-3">
                    <MessageLabel text={"Branch Name"} className="optionalSimple" />
                    <input
                      id="branch_name"
                      type="text"
                      className="form-control"
                      name="branch_name"
                      autoComplete="branch_name"
                      onChange={handleChange}
                      ref={branchNameRef}
                      placeholder="Enter branch name"
                    />
                    {errors.branch_name && <span className="text-danger mt-2 d-block">{errors.branch_name}</span>}
                  </div>

                  {/* Account Number */}
                  <div className="col-md-6 mb-3">
                    <MessageLabel text={"Account Number"} className="mandatorySimple" />
                    <input
                      id="account_number"
                      type="text"
                      className="form-control"
                      name="account_number"
                      autoComplete="account_number"
                      ref={accountNumberRef}
                      onChange={handleChange}
                    />
                    {errors.account_number && <span className="text-danger mt-2 d-block">{errors.account_number}</span>}
                  </div>

                  {/* IFSC Code */}
                  <div className="col-md-6 mb-3">
                    <MessageLabel text={"IFSC Code"} className="mandatorySimple" />
                    <input
                      id="IFSC_Code"
                      type="text"
                      className="form-control"
                      name="IFSC_Code"
                      autoComplete="IFSC_Code"
                      ref={ifscCodeRef}
                      onChange={handleChange}
                    />
                    {errors?.IFSC_Code && <span className="text-danger mt-2 d-block">{errors.IFSC_Code}</span>}
                  </div>
                  {/* UPI ID */}
                  <div className="col-md-6 mb-3">
                    <MessageLabel text={"UPI ID (Optional)"} className="optionalSimple" />
                    <input
                      id="upi_id"
                      type="text"
                      className="form-control"
                      name="upi_id"
                      autoComplete="upi_id"
                      ref={upiIdRef}
                      onChange={handleChange}
                      placeholder="e.g., user@paytm"
                    />
                    {errors?.upi_id && <span className="text-danger mt-2 d-block">{errors.upi_id}</span>}
                  </div>
                  {/* UPI Mobile Number */}
                  <div className="col-md-6 mb-3">
                    <MessageLabel text={"UPI Mobile Number (Optional)"} className="optionalSimple" />
                    <input
                      id="upi_mobile_number"
                      type="text"
                      className="form-control"
                      name="upi_mobile_number"
                      autoComplete="upi_mobile_number"
                      ref={upiMobileRef}
                      value={inputs?.upi_mobile_number}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*$/.test(value) && value.length <= maxMobileLength) {
                          handleChange(e);
                        }
                      }}
                      placeholder={`${maxMobileLength}-digit mobile number`}
                    />
                    {errors.upi_mobile_number && <span className="text-danger mt-2 d-block">{errors.upi_mobile_number}</span>}
                  </div>

                  {/* Navigation Buttons */}
                  <div className="col-12 d-flex justify-content-between align-items-center mt-4">
                    <button
                      type="button"
                      onClick={() => setSwitchTap(0)}
                      className="btn btn-outline-secondary fw-bold px-4"
                      disabled={processing}
                    >
                      ← Previous Step
                    </button>
                    <button
                      type="submit"
                      className="btn btn-success fw-bold px-4"
                      disabled={processing}
                    >
                      {processing ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Creating...
                        </>
                      ) : (
                        "Create Partner"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

    </>
  );
};

export default PartnerCreate;
